import React, { useState, useEffect, useCallback } from 'react';
import { mktFetch, MARKETING_API } from './MarketingHubModal';
import { assetUrl } from './MarketingPlatformPreview.js';

const API = MARKETING_API;

const STATUS_LABEL = {
  active: 'Current',
  kept: 'Recent',
  saved: 'Saved',
  archived: 'Archived',
};

export default function MarketingAssetVersions({ draftId, onChange }) {
  const [versions, setVersions] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!draftId) return;
    mktFetch(`${API}/platform/drafts/${draftId}/versions`)
      .then((r) => r.json())
      .then((d) => { setVersions(d.versions || []); setCurrentId(d.current_asset_id || null); })
      .catch(() => {});
  }, [draftId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const act = async (assetId, action, body) => {
    setBusy(true); setMsg('');
    try {
      const method = action === 'delete' ? 'DELETE' : 'POST';
      const url = action === 'delete'
        ? `${API}/platform/assets/${assetId}`
        : `${API}/platform/assets/${assetId}/${action}`;
      const res = await mktFetch(url, { method, body: body ? JSON.stringify(body) : undefined });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `${action} failed`);
      load();
      onChange?.();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  if (!draftId) return null;

  const archivedCount = versions.filter((v) => v.status === 'archived').length;
  const liveCount = versions.filter((v) => v.status !== 'archived').length;

  return (
    <div className="mkt-versions">
      <button type="button" className="mkt-versions-toggle" onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} Versions ({liveCount} live{archivedCount ? `, ${archivedCount} archived` : ''})
      </button>
      {open && (
        <>
          <div className="mkt-versions-grid">
            {versions.map((v) => {
              const isCurrent = v.id === currentId;
              return (
                <div key={v.id} className={`mkt-version-card${isCurrent ? ' is-current' : ''}`}>
                  <div className="mkt-version-thumb">
                    {v.kind === 'video' ? (
                      <video src={assetUrl(v.id)} muted playsInline preload="metadata" style={{ width: '100%', display: 'block' }} />
                    ) : (
                      <img src={assetUrl(v.id)} alt={v.filename} loading="lazy" />
                    )}
                    <span className={`mkt-version-badge mkt-version-${v.status}`}>
                      {v.starred ? 'Saved' : (STATUS_LABEL[v.status] || v.status)}
                    </span>
                    {isCurrent && <span className="mkt-version-current-tag">USING</span>}
                  </div>
                  {v.status === 'archived' && (
                    <span className="mkt-version-purge">Purges in {v.days_to_purge}d</span>
                  )}
                  <div className="mkt-version-actions">
                    {!isCurrent && (
                      <button type="button" disabled={busy} title="Use this version" onClick={() => act(v.id, 'restore')}>Restore</button>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      title={v.starred ? 'Remove from library' : 'Save to library (never auto-deleted)'}
                      onClick={() => act(v.id, 'star', { starred: !v.starred })}
                    >
                      {v.starred ? '★' : '☆'}
                    </button>
                    {!isCurrent && (
                      <button type="button" className="mkt-version-del" disabled={busy} title="Delete now" onClick={() => act(v.id, 'delete')}>✕</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="marketing-source-meta">Keeps the last 3 live versions for undo. Older versions are archived (compressed) and deleted after 30 days unless saved (★).</p>
          {msg && <p className="marketing-msg-err">{msg}</p>}
        </>
      )}
    </div>
  );
}
